/**
 * API calls for the super-admin Clever connections screen. Connections stand
 * alone as districts; accounts are created by the sync (one per school) and
 * exposed read-only here. All endpoints require the super_admin flag on the
 * backend.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'

import type {
  AccountsPage,
  AdminAccount,
  ConnectionFormValues,
  ConnectionSettingsValues,
  PaginationMeta,
  RosterConnectionDetail,
  RosterConnectionInfo,
  RosterSyncRun,
  RosterSyncRunDetail,
} from '../types'

/**
 * Guard for 401 responses. The axios interceptor does NOT reject on 401
 * (it returns the response directly), so we must check status manually.
 */
function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

function assertOk(response: { status: number; data: unknown }): void {
  assertAuthenticated(response)
  if (response.status >= 400) {
    throw new Error(JSON.stringify(response.data))
  }
}

export async function fetchConnections(): Promise<RosterConnectionInfo[]> {
  const response = await api.get<{ rosterConnections: RosterConnectionInfo[] }>(
    ARC_MOSAICO_ENDPOINTS.rosterConnections.list,
  )
  assertOk(response)
  return Array.isArray(response.data?.rosterConnections) ? response.data.rosterConnections : []
}

export interface FetchAccountsParams {
  page: number
  limit: number
  rosterConnectionId: string
}

export async function fetchAccounts({
  page,
  limit,
  rosterConnectionId,
}: FetchAccountsParams): Promise<AccountsPage> {
  const response = await api.get<{ accounts: AdminAccount[]; pagination?: PaginationMeta }>(
    ARC_MOSAICO_ENDPOINTS.accounts.list,
    { params: { page, limit, rosterConnectionId } },
  )
  assertOk(response)
  return {
    accounts: Array.isArray(response.data?.accounts) ? response.data.accounts : [],
    pagination: response.data?.pagination ?? null,
  }
}

export async function createConnection(
  values: ConnectionFormValues,
): Promise<RosterConnectionInfo> {
  const response = await api.post<RosterConnectionInfo>(
    ARC_MOSAICO_ENDPOINTS.rosterConnections.create,
    {
      districtExternalId: values.districtExternalId,
      districtName: values.districtName,
    },
  )
  assertOk(response)
  return response.data
}

export async function fetchConnection(connectionId: string): Promise<RosterConnectionDetail> {
  const response = await api.get<RosterConnectionDetail>(
    ARC_MOSAICO_ENDPOINTS.rosterConnections.show(connectionId),
  )
  assertOk(response)
  return response.data
}

export async function updateConnection(
  connectionId: string,
  values: ConnectionSettingsValues,
): Promise<void> {
  const response = await api.patch(ARC_MOSAICO_ENDPOINTS.rosterConnections.update(connectionId), values)
  assertOk(response)
}

export async function fetchRunDetail(runId: string): Promise<RosterSyncRunDetail> {
  const response = await api.get<RosterSyncRunDetail>(
    ARC_MOSAICO_ENDPOINTS.rosterConnections.runDetail(runId),
  )
  assertOk(response)
  return response.data
}

export async function triggerRun(
  connectionId: string,
  options: { dryRun?: boolean; forceArchive?: boolean } = {},
): Promise<RosterSyncRun> {
  const response = await api.post<RosterSyncRun>(
    ARC_MOSAICO_ENDPOINTS.rosterConnections.triggerRun(connectionId),
    { dryRun: options.dryRun ?? false, forceArchive: options.forceArchive ?? false },
  )
  assertOk(response)
  return response.data
}
